import { useLayoutEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useMeasure } from '@frameio/vapor';
import { VERTICAL_BORDER_PX } from '../../constants';

const getTextScrollKeyframes = (startCoordinate, endCoordinate) => keyframes`
  from { transform: translateX(${startCoordinate}px); }
  to   { transform: translateX(${endCoordinate}px); }
`;

const NON_BREAKING_SPACE = '\u00A0';

type WrapperProps = {
  isAnimationPaused: boolean;
  isReverse: boolean;
  targetPosition: number;
  blockWidth: number;
};

const Wrapper = styled.div<WrapperProps>`
  animation-delay: -${(props) => 20 * (props.isAnimationPaused ? 0.5 : 0.25)}s;
  animation-duration: 20s;
  animation-iteration-count: infinite;
  animation-name: ${(props) =>
    props.isReverse
      ? (getTextScrollKeyframes(
          Math.round(props.targetPosition),
          -props.blockWidth
        ) as unknown as string)
      : (getTextScrollKeyframes(
          -props.blockWidth,
          Math.round(props.targetPosition)
        ) as unknown as string)};
  animation-play-state: ${(props) =>
    props.isAnimationPaused ? 'paused' : 'running'};
  animation-timing-function: linear;
  position: absolute;
  top: ${VERTICAL_BORDER_PX}px;
`;

type WatermarkTemplateBlockMarqueeProps = {
  children: React.ReactNode;
  isAnimationPaused: boolean;
  isReverse: boolean;
  scaleX: number;
  targetPosition: number;
};

const WatermarkTemplateBlockMarquee = ({
  children,
  isAnimationPaused,
  isReverse,
  scaleX,
  targetPosition,
}: WatermarkTemplateBlockMarqueeProps) => {
  const [measureRef, measureRect] = useMeasure();
  const [blockWidth, setBlockWidth] = useState(0);

  useLayoutEffect(() => {
    setBlockWidth(Math.round(measureRect.width));
  }, [measureRect.width, scaleX]);

  return (
    <>
      <ScaledSpan isReverse={isReverse} scaleX={scaleX}>
        {NON_BREAKING_SPACE}
      </ScaledSpan>
      <Wrapper
        blockWidth={blockWidth}
        isAnimationPaused={isAnimationPaused}
        isReverse={isReverse}
        targetPosition={targetPosition}
      >
        <ScaledSpan isReverse={isReverse} scaleX={scaleX}>
          <span ref={measureRef}>{children}</span>
        </ScaledSpan>
      </Wrapper>
    </>
  );
};

export default WatermarkTemplateBlockMarquee;

const ScaledSpan = styled.span<{ isReverse: boolean; scaleX: number }>`
  display: inline-block;
  transform: ${(p) => `scaleX(${p.scaleX})`};
  transform-origin: ${(p) => (p.isReverse ? 'left' : 'right')};
`;
